import { ListItemDisclosure } from '@euler/app/components/list/ListItemDisclosure';
import { Typography } from '@euler/app/components/typography';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { useServiceRecordNavigation } from '@euler/app/flows/record/functions/useServiceRecordNavigation';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { Colors, StatusColors } from '@euler/components/Colors';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CommonTaskStatus,
  QuotationFormStatus,
  StoreFeature,
} from '@euler/model/enum';
import { QuotationForm, SettlementForm } from '@euler/model/task-detail';
import { ReactNode, memo, useCallback } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const QuotationSection = memo(() => {
  const isQuotationEnabled = useStoreFeature(StoreFeature.Quotation).enabled;
  const { serviceRecord } = useServiceRecordContext();
  const { rpx } = useResponsiveContext();

  const navigation = useServiceRecordNavigation();

  const onViewQuotationForm = useCallback(() => {
    navigation.push('_quotationForm', {});
  }, [navigation]);

  const onViewSettlementForm = useCallback(() => {
    navigation.push('_settlementForm', {});
  }, [navigation]);

  if (!isQuotationEnabled && serviceRecord.quotationForm == null) {
    return null;
  }

  return (
    <TaskDetailSection
      title="维修报价"
      noBodyPadding={serviceRecord.quotationForm != null}
      css={`
        margin-bottom: ${rpx(15)}px;
      `}
    >
      {serviceRecord.quotationForm == null ? (
        <Typography.Text>没有维修报价/结算相关信息</Typography.Text>
      ) : (
        <>
          <Cell
            title="查看报价单"
            detail={<QuotationFormInfo form={serviceRecord.quotationForm} />}
            onPress={onViewQuotationForm}
          />
          {serviceRecord.settlementForm != null &&
          serviceRecord.settlementStatus === CommonTaskStatus.Finished ? (
            <>
              <Separator />
              <Cell
                title="查看结算单"
                detail={
                  <SettlementFormInfo form={serviceRecord.settlementForm} />
                }
                onPress={onViewSettlementForm}
              />
            </>
          ) : null}
        </>
      )}
    </TaskDetailSection>
  );
});

const QuotationFormInfo = memo(({ form }: { form: QuotationForm }) => {
  const { rpx } = useResponsiveContext();
  if (form.status === QuotationFormStatus.Confirmed) {
    return (
      <Typography.Text size={rpx(14)} color={StatusColors.Success}>
        ￥{(form.computedTotalFee / 100).toFixed(2)}
      </Typography.Text>
    );
  }
  if (form.computedTotalFee === -1) {
    return <Typography.Text color={Colors.Gray3}>未确认</Typography.Text>;
  }
  return (
    <Typography.Text color={Colors.Gray3}>
      {`未确认 (￥${(form.computedTotalFee / 100).toFixed(2)})`}
    </Typography.Text>
  );
});

const SettlementFormInfo = memo(({ form }: { form: SettlementForm }) => {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text size={rpx(14)} color={StatusColors.Success}>
      ￥{(form.computedTotalFee / 100).toFixed(2)}
    </Typography.Text>
  );
});

const Cell = memo(
  ({
    title,
    detail,
    style,
    onPress,
  }: {
    title: string;
    detail?: ReactNode;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: ${rpx(10)}px ${rpx(6)}px ${rpx(10)}px ${rpx(15)}px;
        `}
        style={style}
        onPress={onPress}
      >
        <Typography.Label regular size={rpx(16)} flex={1}>
          {title}
        </Typography.Label>
        {detail}
        <ListItemDisclosure />
      </TouchableOpacity>
    );
  },
);

const Separator = memo(() => {
  return (
    <View
      css={`
        height: ${StyleSheet.hairlineWidth}px;
        background-color: #ddd;
      `}
    />
  );
});
